import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { calculationDetailPath, routePaths } from "@/constants/routePaths";
import type { AddAppToastInput } from "@/components/AppToast";
import CalculationInputList from "./CalculationInputList/CalculationInputList.component";
import CalculationInputFilters from "./CalculationInputFilters/CalculationInputFilters.component";
import CalculationInputEmptyState from "./CalculationInputEmptyState/CalculationInputEmptyState.component";
import CalculationInputNoMatches from "./CalculationInputNoMatches/CalculationInputNoMatches.component";
import CreateCalculationPanel from "./CreateCalculationPanel/CreateCalculationPanel.component";
import DeleteCalculationConfirmationPopup from "./DeleteCalculationConfirmationPopup/DeleteCalculationConfirmationPopup.component";
import type { CalculationFilters } from "./CalculationInputList/CalculationInputList.types";
import {
  hasActiveFilters,
  computeAddDisabled,
  computeAddDisabledReason,
} from "./CalculationInputList/CalculationInputList.types";
import {
  listStateToSearchParams,
  searchParamsToPagination,
  searchParamsToFilters,
} from "./CalculationInputList/listStateSearchParams";
import {
  emptyFilters,
  pageSizeOptions,
} from "./CalculationInputList/CalculationInputList.constants";
import useCalculationEntries, {
  toCalculationFilterInput,
} from "../hooks/useCalculationEntries";
import useFilterOptions from "../hooks/useFilterOptions";
import useDeleteCalculationConfirmation from "../hooks/useDeleteCalculationConfirmation";
import useSuppliers from "../hooks/useSuppliers";
import DelayedSpinner from "@/components/DelayedSpinner";

const getCalculationListPath = (searchParams: URLSearchParams): string => {
  const query = searchParams.toString();
  return query
    ? `${routePaths.calculations}?${query}`
    : routePaths.calculations;
};

const CalculationInputListContainer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const applied = useMemo(
    () => searchParamsToFilters(searchParams),
    [searchParams],
  );
  const { currentPage, pageSize } = useMemo(
    () => searchParamsToPagination(searchParams),
    [searchParams],
  );

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [refreshEpoch, setRefreshEpoch] = useState(0);

  const { entries, pageInfo, loading, called, refetch } =
    useCalculationEntries();
  const { kamNames: kamOptions } = useFilterOptions();
  const { suppliers, loading: suppliersLoading } = useSuppliers();
  const suppliersGate = { loading: suppliersLoading, count: suppliers.length };
  const addDisabled = computeAddDisabled(suppliersGate);
  const addDisabledReason = computeAddDisabledReason(suppliersGate);
  const displayTotalPages = useMemo(
    () => Math.max(Math.ceil(pageInfo.totalElements / pageSize), 1),
    [pageInfo.totalElements, pageSize],
  );
  const displayPage = useMemo(
    () =>
      pageInfo.totalElements === 0
        ? 1
        : Math.min(currentPage + 1, displayTotalPages),
    [currentPage, displayTotalPages, pageInfo.totalElements],
  );
  const shouldClampUrlPage =
    called &&
    !loading &&
    pageInfo.totalElements > 0 &&
    currentPage >= displayTotalPages;
  const hasOutOfRangePageResponse =
    called &&
    !loading &&
    pageInfo.totalElements > 0 &&
    pageInfo.currentPage >= displayTotalPages;
  const isOutOfRangePage = shouldClampUrlPage || hasOutOfRangePageResponse;

  useEffect(() => {
    refetch(toCalculationFilterInput(applied), currentPage, pageSize);
  }, [applied, currentPage, pageSize, refreshEpoch, refetch]);

  useEffect(() => {
    if (!shouldClampUrlPage) return;

    setSearchParams(
      listStateToSearchParams(applied, displayTotalPages - 1, pageSize),
      { replace: true },
    );
  }, [
    applied,
    displayTotalPages,
    pageSize,
    setSearchParams,
    shouldClampUrlPage,
  ]);

  const setAppliedField = useCallback(
    <K extends keyof CalculationFilters>(
      field: K,
      value: CalculationFilters[K],
    ) => {
      const nextFilters = { ...applied, [field]: value };

      setSearchParams(listStateToSearchParams(nextFilters, 0, pageSize));
    },
    [applied, pageSize, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(listStateToSearchParams(emptyFilters, 0, pageSize));
  }, [pageSize, setSearchParams]);

  const handleAdd = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const handleCreateSuccess = useCallback(
    (id: string) => {
      setIsPanelOpen(false);
      navigate(calculationDetailPath(id), {
        state: { returnTo: getCalculationListPath(searchParams) },
      });
    },
    [navigate, searchParams],
  );

  const handleOpen = useCallback(
    (id: string) => {
      navigate(calculationDetailPath(id), {
        state: { returnTo: getCalculationListPath(searchParams) },
      });
    },
    [navigate, searchParams],
  );

  const handleCopy = useCallback(() => {
    // TODO: duplicate calculation
  }, []);

  const refreshList = useCallback(() => {
    setRefreshEpoch((epoch) => epoch + 1);
  }, []);

  const deleteFailureToast = useMemo<AddAppToastInput>(
    () => ({
      appearance: "danger",
      closeLabel: "Dismiss delete error",
      message: "Failed to delete calculation.",
    }),
    [],
  );

  const deleteConfirmation = useDeleteCalculationConfirmation({
    applied,
    currentPage,
    pageSize,
    totalElements: pageInfo.totalElements,
    setSearchParams,
    onRefresh: refreshList,
    deleteFailureToast,
  });

  const handlePageSizeChange = useCallback(
    (selectedPageSize: number) => {
      if (selectedPageSize === pageSize) return;
      setSearchParams(listStateToSearchParams(applied, 0, selectedPageSize));
    },
    [applied, pageSize, setSearchParams],
  );

  const handlePageSelect = useCallback(
    (page: number) => {
      setSearchParams(listStateToSearchParams(applied, page, pageSize));
    },
    [applied, pageSize, setSearchParams],
  );

  const handlePreviousPage = useCallback(() => {
    setSearchParams(
      listStateToSearchParams(applied, Math.max(currentPage - 1, 0), pageSize),
    );
  }, [applied, currentPage, pageSize, setSearchParams]);

  const handleNextPage = useCallback(() => {
    setSearchParams(
      listStateToSearchParams(
        applied,
        Math.min(currentPage + 1, Math.max(displayTotalPages - 1, 0)),
        pageSize,
      ),
    );
  }, [applied, currentPage, displayTotalPages, pageSize, setSearchParams]);

  const isDayZero =
    called &&
    entries.length === 0 &&
    !hasActiveFilters(applied) &&
    !loading &&
    !isOutOfRangePage;
  const isFilteredEmpty =
    called &&
    entries.length === 0 &&
    hasActiveFilters(applied) &&
    !loading &&
    !isOutOfRangePage;
  const showList = entries.length > 0 || isFilteredEmpty;
  const isLoading = !called || loading;
  const pagination = useMemo(
    () => ({
      pageSize,
      pageSizeOptions,
      totalElements: pageInfo.totalElements,
      displayPage,
      displayTotalPages,
      // Pagination only renders once loading is false (the spinner replaces
      // the table + controls while loading), so no loading guard is needed here.
      navigationDisabled: false,
      canGoPrevious: currentPage > 0,
      canGoNext: currentPage < displayTotalPages - 1,
      onPageSizeChange: handlePageSizeChange,
      onPageSelect: handlePageSelect,
      onPreviousPage: handlePreviousPage,
      onNextPage: handleNextPage,
    }),
    [
      currentPage,
      displayPage,
      displayTotalPages,
      handleNextPage,
      handlePageSelect,
      handlePageSizeChange,
      handlePreviousPage,
      pageInfo.totalElements,
      pageSize,
    ],
  );

  return (
    <>
      {!isDayZero && (
        <CalculationInputFilters
          draft={applied}
          kamOptions={kamOptions}
          supplierOptions={suppliers}
          onSetField={setAppliedField}
          onAdd={handleAdd}
          addDisabled={addDisabled}
          addDisabledReason={addDisabledReason}
        />
      )}
      {isLoading ? (
        <DelayedSpinner />
      ) : (
        <>
          {showList && (
            <CalculationInputList
              entries={entries}
              pagination={pagination}
              deleteTargetRef={deleteConfirmation.targetRef}
              onOpen={handleOpen}
              onCopy={handleCopy}
              onDelete={deleteConfirmation.requestDelete}
            />
          )}
          {isFilteredEmpty && (
            <CalculationInputNoMatches onClearFilters={handleClearFilters} />
          )}
          {isDayZero && <CalculationInputEmptyState onCreate={handleAdd} />}
        </>
      )}
      <CreateCalculationPanel
        active={isPanelOpen}
        onClose={handlePanelClose}
        onSuccess={handleCreateSuccess}
      />
      <DeleteCalculationConfirmationPopup
        candidate={deleteConfirmation.candidate}
        active={deleteConfirmation.active}
        loading={deleteConfirmation.loading}
        targetRef={deleteConfirmation.targetRef}
        onCancel={deleteConfirmation.cancelDelete}
        onConfirm={deleteConfirmation.confirmDelete}
      />
    </>
  );
};

export default CalculationInputListContainer;
